import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import GlassCard from './GlassCard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  MoreVertical,
  Edit,
  Eye,
  Trash2,
  Lock,
  Share2,
  Check,
  Copy,
  LayoutGrid,
  Star,
  Download,
} from 'lucide-react';

/**
 * Unified Skill Card Component
 * 
 * @param {Object} skill - The skill object
 * @param {boolean} showActions - Show the actions dropdown (default: false)
 * @param {Function} onEdit - Callback when edit is clicked
 * @param {Function} onDelete - Callback when delete is clicked
 * @param {Function} onCategoryChange - Callback when category is clicked
 * @param {Function} onVisibilityChange - Callback when visibility is changed
 * @param {boolean} showSlug - Show the skill slug (default: false)
 * @param {boolean} showStatus - Show status badge (default: false)
 */
const SkillCard = ({
  skill,
  showActions = false,
  onEdit,
  onDelete,
  onCategoryChange,
  onVisibilityChange,
  showSlug = false,
  showStatus = false,
}) => {
  const navigate = useNavigate();
  const [copiedSkillId, setCopiedSkillId] = useState(null);

  const statusConfig = {
    DRAFT: {
      label: 'Draft',
      icon: <Edit className="h-3 w-3" />,
      className: 'bg-gray-100 text-gray-700 dark:bg-dark-bg-tertiary dark:text-gray-300 border-gray-300 dark:border-[#2e2e2d]'
    },
    PRIVATE: {
      label: 'Private',
      icon: <Lock className="h-3 w-3" />,
      className: 'bg-gray-100 text-gray-700 dark:bg-dark-bg-tertiary dark:text-gray-300 border-gray-300 dark:border-[#2e2e2d]'
    },
    UNLISTED: {
      label: 'Unlisted',
      icon: <Eye className="h-3 w-3" />,
      className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400 border-orange-300'
    },
    PUBLIC: {
      label: 'Public',
      icon: <Eye className="h-3 w-3" />,
      className: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400 border-green-300'
    },
  };

  const handleCopyLink = (skill) => {
    const url = `${window.location.origin}/skills/${skill.id}`;
    navigator.clipboard.writeText(url);
    setCopiedSkillId(skill.id);
    setTimeout(() => setCopiedSkillId(null), 2000);
  };

  const handleExportMd = (skill) => {
    const blob = new Blob([skill.skill_markdown || ''], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${skill.slug || skill.name}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  let status;
  if (!skill.is_published || skill.visibility === 'DRAFT') {
    status = 'DRAFT';
  } else {
    status = skill.visibility || 'PUBLIC';
  }
  const config = statusConfig[status];

  return (
    <GlassCard hover className="p-6 relative group">
      {/* Actions Dropdown */}
      {showActions && (
        <div className="absolute top-4 right-4 z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass-strong w-48">
              <DropdownMenuItem onClick={() => onEdit?.(skill)} className="cursor-pointer">
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/skills/${skill.id}`)} className="cursor-pointer">
                <Eye className="h-4 w-4 mr-2" />
                View
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => onCategoryChange?.(skill)}
                className="cursor-pointer"
              >
                <LayoutGrid className="h-4 w-4 mr-2" />
                Category
              </DropdownMenuItem>

              {/* Visibility Submenu - only for published skills */}
              {skill.visibility !== 'DRAFT' && onVisibilityChange && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer">
                    <Lock className="h-4 w-4 mr-2" />
                    Visibility
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent className="bg-white dark:bg-dark-bg-primary border border-gray-200 dark:border-[#2e2e2d] shadow-lg">
                      <DropdownMenuItem
                        onClick={() => onVisibilityChange(skill.id, 'PRIVATE')}
                        className={`cursor-pointer ${skill.visibility === 'PRIVATE' ? 'bg-accent' : ''}`}
                      >
                        {skill.visibility === 'PRIVATE' && <Check className="h-4 w-4 mr-2" />}
                        {skill.visibility !== 'PRIVATE' && <span className="w-6" />}
                        Private
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onVisibilityChange(skill.id, 'UNLISTED')}
                        className={`cursor-pointer ${skill.visibility === 'UNLISTED' ? 'bg-accent' : ''}`}
                      >
                        {skill.visibility === 'UNLISTED' && <Check className="h-4 w-4 mr-2" />}
                        {skill.visibility !== 'UNLISTED' && <span className="w-6" />}
                        Unlisted
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onVisibilityChange(skill.id, 'PUBLIC')}
                        className={`cursor-pointer ${skill.visibility === 'PUBLIC' ? 'bg-accent' : ''}`}
                      >
                        {skill.visibility === 'PUBLIC' && <Check className="h-4 w-4 mr-2" />}
                        {skill.visibility !== 'PUBLIC' && <span className="w-6" />}
                        Public
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              )}

              {/* Share Submenu */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="cursor-pointer">
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="bg-white dark:bg-dark-bg-primary border border-gray-200 dark:border-[#2e2e2d] shadow-lg">
                    <DropdownMenuItem
                      onClick={() => handleCopyLink(skill)}
                      className="cursor-pointer"
                    >
                      {copiedSkillId === skill.id ? (
                        <>
                          <Check className="h-4 w-4 mr-2 text-green-600" />
                          Link Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy Link
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleExportMd(skill)}
                      className="cursor-pointer"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export .md
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>

              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete?.(skill)}
                className="cursor-pointer text-destructive"
                style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}
              >
                <Trash2 className="h-4 w-4 mr-2" style={{ flexShrink: 0 }} />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Skill Content */}
      <Link to={`/skills/${skill.id}`} className="block">
        {/* Title */}
        <h3 className="text-xl font-manrope font-bold text-text-dark dark:text-dark-text mb-2 pr-8">
          {skill.name}
        </h3>

        {/* Slug (optional) */}
        {showSlug && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 font-mono">
            {skill.slug}
          </p>
        )}

        {/* Badges: Status + Category */}
        {(showStatus || skill.category || skill.category_id) && (
          <div className="flex items-center gap-2 mb-4">
            {showStatus && config && (
              <Badge className={`flex items-center gap-1 border text-xs ${config.className}`}>
                {config.icon}
                <span>{config.label}</span>
              </Badge>
            )}

            {(skill.category || skill.category_id) && (
              <Badge className="bg-primary text-white border-0 text-xs font-medium">
                {typeof skill.category === 'object' ? skill.category.name : (skill.category || skill.category_id)}
              </Badge>
            )}
          </div>
        )}

        {/* Description */}
        <p className="text-sm text-text-gray dark:text-dark-text-secondary line-clamp-2 mb-4">
          {skill.description || 'No description'}
        </p>

        {/* Stats */}
        <div className="flex items-center gap-4 text-sm text-text-gray dark:text-dark-text-secondary">
          <div className="flex items-center gap-1">
            <Star className="h-4 w-4" />
            <span>{skill.star_count || 0}</span>
          </div>
          <div className="flex items-center gap-1">
            <Download className="h-4 w-4" />
            <span>{skill.install_count || 0}</span>
          </div>
        </div>
      </Link>
    </GlassCard>
  );
};

export default SkillCard;
